<template>
  <section class="insights-sec sec sec-tint">
    <div class="page">
      <SectionHeading
        eyebrow="Insights"
        title="行业洞察"
        desc="把一线实施里踩过的坑、算过的账、验收过的标准写下来，方便你拿去和内部讨论。"
      />

      <div class="ins-grid">
        <router-link
          v-for="(p, i) in latest"
          :key="p.slug"
          :to="`/insights/${p.slug}`"
          class="ins-item reveal"
          v-reveal="{ delay: i * 70 }"
        >
          <span class="ins-bar"></span>
          <div class="ins-head">
            <span class="ins-chip">{{ p.category }}</span>
            <span class="ins-mins">{{ p.minutes }} 分钟</span>
          </div>
          <h3 class="ins-name">{{ p.title }}</h3>
          <p class="ins-desc">{{ p.description }}</p>
          <span class="ins-more">阅读全文 <IconSvg name="arrow-right"/></span>
        </router-link>
      </div>

      <div class="ins-all">
        <router-link to="/insights" class="btn btn-ghost">
          查看全部 {{ total }} 篇
          <IconSvg name="arrow-right"/>
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
import SectionHeading from '@/components/common/SectionHeading'
import IconSvg from '@/components/common/IconSvg'
import { posts } from '@/data/posts'

/**
 * 首页「行业洞察」区：把站内长文挂到首页。
 * 首页是权重最高的页面，从这里指出去的内链对文章收录帮助最大；
 * 同时首页有持续更新的内容，对「网站活跃度」这个排序因子也有正向作用。
 */
export default {
  name: 'InsightsSection',
  components: { SectionHeading, IconSvg },
  data() {
    return { posts }
  },
  computed: {
    total() {
      return this.posts.length
    },
    /**
     * 首页展示的文章：按业务线轮流取，保证每条线都有露脸机会。
     *
     * ⚠️ why：posts 的排序是「MES 优先」，直接 slice(0, 3) 会让首页
     *    **永远只展示 MES 的三篇**，智慧农业三篇一篇都上不了首页——
     *    全站权重最高的页面只把链接导给了一半内容，链接分布也严重不均。
     *    改为按分类轮流取，两类业务线都能被首页链到。
     */
    latest() {
      const buckets = {}
      this.posts.forEach((p) => {
        const k = p.categoryKey || 'other'
        if (!buckets[k]) buckets[k] = []
        buckets[k].push(p)
      })
      // 智慧农业是旗舰业务线，排在首位；其余分类保持出现顺序
      const keys = Object.keys(buckets)
      const order = keys.indexOf('agriculture') !== -1
        ? ['agriculture'].concat(keys.filter((k) => k !== 'agriculture'))
        : keys

      const LIMIT = 3
      const out = []
      let i = 0
      while (out.length < LIMIT) {
        let added = false
        for (const k of order) {
          if (buckets[k][i]) {
            out.push(buckets[k][i])
            added = true
          }
          if (out.length >= LIMIT) break
        }
        if (!added) break
        i += 1
      }
      return out
    }
  }
}
</script>

<style lang="scss" scoped>
.ins-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
}

.ins-item {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 26px 26px 24px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, rgba(13, 27, 54, 0.86), rgba(6, 13, 29, 0.86));
  overflow: hidden;
  transition: transform 0.45s cubic-bezier(0.2, 0.7, 0.2, 1), border-color 0.45s, box-shadow 0.45s;

  .ins-bar {
    position: absolute;
    left: 0;
    top: 26px;
    bottom: 26px;
    width: 2px;
    border-radius: 2px;
    background: linear-gradient(180deg, var(--cyan), transparent);
    opacity: 0.5;
    transition: opacity 0.45s;
  }

  &:hover {
    transform: translateY(-8px);
    border-color: var(--line-strong);
    box-shadow: 0 30px 60px -34px rgba(34, 224, 255, 0.6);
    .ins-bar { opacity: 1; }
    .ins-more svg { transform: translateX(4px); }
  }
}

.ins-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.ins-chip {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  color: var(--cyan);
  background: rgba(34, 224, 255, 0.1);
  border: 1px solid rgba(34, 224, 255, 0.22);
}

.ins-mins { font-size: 12px; color: var(--txt-3); }

.ins-name {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.55;
  color: #fff;
}

.ins-desc {
  margin: 0 0 20px;
  flex: 1;
  font-size: 14px;
  line-height: 1.85;
  color: var(--txt-2);
}

.ins-more {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--accent-txt);
  svg { width: 15px; height: 15px; transition: transform 0.35s; }
}

.ins-all {
  display: flex;
  justify-content: center;
  margin-top: 38px;

  .btn { height: 46px; padding: 0 26px; font-size: 14.5px; }
}

@media (max-width: 1080px) {
  .ins-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 760px) {
  .ins-grid { grid-template-columns: minmax(0, 1fr); gap: 16px; }
  .ins-item { padding: 22px; }
}
</style>
